import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, Trash2, Plus, AlertTriangle, ListPlus } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import Secao from "../WhatsAppModal/Secao";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto")
		.max(50, "Muito longo")
		.required("Campo obrigatório"),
	number: Yup.string().min(8, "Muito curto").max(50, "Muito longo"),
	email: Yup.string().email("E-mail inválido"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
	};

	const [contact, setContact] = useState(initialState);
	// Campos extras são exceção: quase ninguém usa, e em aberto eles roubam a
	// atenção de nome e número, que é o que importa.
	const [extrasAbertos, setExtrasAbertos] = useState(false);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact(data);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const handleSaveContact = async values => {
		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, values);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", values);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{contactId ? "Editar contato" : "Novo contato"}
					</DialogTitle>
					<DialogDescription>
						{contactId
							? "Muda só o cadastro na sua agenda. O contato não é avisado e nada é enviado para ele."
							: "Cadastrar aqui não avisa ninguém e não manda mensagem nenhuma. Só deixa a pessoa pronta na agenda, para você abrir a conversa quando quiser."}
					</DialogDescription>
				</DialogHeader>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting }) => (
						<Form className="space-y-4">
							<div className="flex gap-3">
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="name">{i18n.t("contactModal.form.name")}</Label>
									<Field
										as={Input}
										id="name"
										name="name"
										autoFocus
										placeholder="Ex.: Maria Silva"
									/>
									{touched.name && errors.name && (
										<p className="text-xs text-destructive">{errors.name}</p>
									)}
									<p className="text-xs leading-relaxed text-muted-foreground">
										É o que a equipe vê na lista de conversas.
									</p>
								</div>
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="number">{i18n.t("contactModal.form.number")}</Label>
									<Field as={Input} id="number" name="number" placeholder="5511987654321" />
									{touched.number && errors.number && (
										<p className="text-xs text-destructive">{errors.number}</p>
									)}
									<p className="text-xs leading-relaxed text-muted-foreground">
										Código do país, DDD e o número, tudo junto e só números:
										55, 11, 987654321.
									</p>
								</div>
							</div>

							{/* Contato sem número existe no banco e não serve para nada:
							    não dá para abrir conversa com ele. */}
							{!values.number && (
								<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
									<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
									Sem o número do WhatsApp o contato fica só na agenda: não dá
									para abrir conversa com ele nem incluí-lo em disparos.
								</p>
							)}

							<div className="space-y-1.5">
								<Label htmlFor="email">{i18n.t("contactModal.form.email")}</Label>
								<Field
									as={Input}
									id="email"
									name="email"
									placeholder="maria@empresa.com.br"
								/>
								{touched.email && errors.email && (
									<p className="text-xs text-destructive">{errors.email}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									Opcional. Fica guardado como referência — o sistema não manda
									e-mail.
								</p>
							</div>

							<Secao
								titulo="Outras informações deste contato"
								resumo={
									values.extraInfo?.length
										? `${values.extraInfo.length} ${
												values.extraInfo.length === 1
													? "informação guardada"
													: "informações guardadas"
										  }`
										: "Nenhuma informação extra"
								}
								icone={ListPlus}
								aberta={extrasAbertos}
								onToggle={() => setExtrasAbertos((v) => !v)}
							>
								<p className="text-xs leading-relaxed text-muted-foreground">
									Anotações suas sobre a pessoa — CPF, empresa, número do
									pedido. Aparecem para a equipe ao abrir o contato e não são
									enviadas para ninguém.
								</p>

								<FieldArray name="extraInfo">
								{({ push, remove }) => (
									<div className="space-y-2">
										{values.extraInfo &&
											values.extraInfo.length > 0 &&
											values.extraInfo.map((info, index) => (
												<div key={`${index}-info`} className="flex items-center gap-2">
													<Field
														as={Input}
														placeholder="Ex.: Empresa"
														name={`extraInfo[${index}].name`}
													/>
													<Field
														as={Input}
														placeholder="Ex.: Padaria do Zé"
														name={`extraInfo[${index}].value`}
													/>
													<Button
														type="button"
														variant="ghost"
														size="icon"
														onClick={() => remove(index)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											))}
										<Button
											type="button"
											variant="outline"
											size="sm"
											className="w-full"
											onClick={() => push({ name: "", value: "" })}
										>
											<Plus className="h-3.5 w-3.5" />
											{i18n.t("contactModal.buttons.addExtraInfo")}
										</Button>
									</div>
								)}
								</FieldArray>
							</Secao>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{contactId ? "Salvar alterações" : "Criar contato"}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default ContactModal;
